import type { ReactNode } from "react";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { useController } from "react-hook-form";
import type { FieldValues, FieldPath } from "react-hook-form";
import type { Personne } from "models";
import { stringUtil } from "@sdeapps/react-core";
import InputSkeleton from "components/InputSkeleton";
import { elusUtil, type ControlledInput } from "utils";

const filterOptions = createFilterOptions({
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  stringify: (option: Personne) =>
    stringUtil.normalize(`${option.sexe} ${option.nom} ${option.prenom}`),
});

interface ControlledPersonneAutoCompleteProps<
  T extends FieldValues,
  Name extends FieldPath<T>,
> extends ControlledInput<T, Name> {
  label: string;
  personnes: Array<Personne>;
  isLoading?: boolean;
  noOptionsText?: string;
}

export function ControlledPersonneAutoComplete<T extends FieldValues, Name extends FieldPath<T>>({
  name,
  label,
  control,
  rules,
  personnes,
  isLoading = false,
  noOptionsText,
}: Readonly<ControlledPersonneAutoCompleteProps<T, Name>>): ReactNode {
  const {
    field: { onChange, ref, value },
    fieldState: { error },
  } = useController({ name, control, rules });

  if (isLoading) {
    return <InputSkeleton />;
  }

  return (
    <Autocomplete
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      options={personnes}
      filterOptions={filterOptions}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      value={value}
      getOptionLabel={(personne) => elusUtil.getPersonneString(personne)}
      getOptionKey={({ id }) => id}
      renderOption={(props, personne: Personne) => (
        <li {...props} key={props.key}>
          {elusUtil.getPersonneString(personne)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? "Personne *"}
          placeholder="Nicolas Schmidt"
          inputRef={ref}
          error={error != null}
          helperText={error?.message}
        />
      )}
      noOptionsText={noOptionsText}
    />
  );
}
